<template>
  <div class="stops-sidebar" :key="language">
    <div class="sidebar-header bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-4 rounded-t-lg border-b-2 border-blue-200 dark:border-blue-700">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
          <span class="text-xl">🗺️</span>
        </div>
        <div>
          <h2 class="text-lg font-bold text-blue-900 dark:text-blue-100">
            {{ stopsTitleText }}
          </h2>
          <p class="text-sm text-blue-600 dark:text-blue-300">
            📍 {{ stops.length }} {{ t('stops') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Vehicle Selector -->
    <div v-if="vehicles.length > 0 && !isInDemoMode" class="px-3 mt-3 mb-3">
      <VehicleSelector
        :vehicles="vehicles"
        :model-value="selectedVehicleId"
        @update:model-value="$emit('update:vehicleId', $event)"
        @add-vehicle="$emit('add-vehicle')"
      />
    </div>

    <div class="sidebar-content">
      <!-- No stops message -->
      <div v-if="stops.length === 0" class="text-center py-8 mx-3">
        <div class="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 rounded-lg p-8 border-2 border-dashed border-gray-300 dark:border-gray-600">
          <div class="text-5xl mb-4">🗺️</div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {{ noStopsText }}
          </h3>
          <p class="text-gray-600 dark:text-gray-400 text-sm">
            {{ createFirstTripText }}
          </p>
        </div>
      </div>

      <div v-else class="space-y-2">
        <draggable
          v-model="localStops"
          @end="onDragEnd"
          item-key="id"
          handle=".drag-handle"
          :disabled="isInDemoMode"
        >
          <template #item="{ element: stop, index }">
            <div
              class="stop-item group bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-800/80 rounded-lg border-2 border-gray-200 dark:border-gray-600 p-3 mb-3 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all"
              @click="$emit('stopClick', stop)"
            >
              <div class="flex items-start gap-2">
                <!-- Drag handle (hidden in demo mode) -->
                <div v-if="!isInDemoMode" class="drag-handle cursor-move text-gray-400 hover:text-blue-500 transition-colors mt-1">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 16a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"/>
                  </svg>
                </div>

                <!-- Order number -->
                <div class="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                  {{ index + 1 }}
                </div>

                <!-- Stop info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-xl">{{ getStopIcon(stop.type) }}</span>
                    <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {{ stop.name }}
                    </h3>
                    <!-- Start/Finish badges -->
                    <span v-if="index === 0" class="flex-shrink-0 inline-flex items-center gap-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-md">
                      🏁 {{ t('start') }}
                    </span>
                    <span v-if="index === stops.length - 1 && stops.length > 1" class="flex-shrink-0 inline-flex items-center gap-1 bg-gradient-to-r from-red-500 to-rose-600 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-md">
                      🎯 {{ t('finish') }}
                    </span>
                  </div>
                  
                  <p class="text-xs text-gray-600 dark:text-gray-400 truncate">
                    📍 {{ stop.address }}
                  </p>
                  
                  <div class="flex items-center gap-2 mt-2 text-xs">
                    <span class="inline-flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full font-medium">
                      <span class="flex-shrink-0">⏱️</span>
                      <span>{{ stop.duration }}min</span>
                    </span>
                    <span v-if="index > 0 && calculation" class="inline-flex items-center gap-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full font-medium">
                      <span class="flex-shrink-0">🛣️</span>
                      <span>{{ Math.round(getSegmentDistance(index - 1) / 1000) }}km</span>
                    </span>
                  </div>
                </div>

                <!-- Actions (hidden in demo mode) -->
                <div v-if="!isInDemoMode" class="flex-shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    @click.stop="$emit('editStop', stop)"
                    class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                    title="Edit stop"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button
                    @click.stop="$emit('deleteStop', stop.id)"
                    class="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                    title="Delete stop"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <!-- Trip summary -->
    <div v-if="calculation && stops.length > 1" class="border-t-2 border-gray-200 dark:border-gray-600 p-3">
      <!-- Route info -->
      <div class="mb-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg p-3 border-2 border-blue-200 dark:border-blue-700">
        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <span class="text-lg">🏁</span>
            <div class="flex-1 min-w-0">
              <div class="text-xs text-blue-600 dark:text-blue-300 font-semibold">{{ t('start') }}</div>
              <div class="text-sm text-gray-900 dark:text-gray-100 truncate font-medium">{{ stops[0].name }}</div>
            </div>
          </div>
          <div class="flex items-center justify-center text-gray-400">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-lg">🎯</span>
            <div class="flex-1 min-w-0">
              <div class="text-xs text-red-600 dark:text-red-300 font-semibold">{{ t('finish') }}</div>
              <div class="text-sm text-gray-900 dark:text-gray-100 truncate font-medium">{{ stops[stops.length - 1].name }}</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Statistics -->
      <div class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg p-3 border-2 border-green-200 dark:border-green-700">
        <div class="flex items-center gap-2 mb-3">
          <span class="text-xl">📊</span>
          <h3 class="text-sm font-bold text-green-900 dark:text-green-100">
            {{ tripSummaryText }}
          </h3>
        </div>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">🛣️ {{ distanceText }}</div>
            <div class="font-bold text-green-600 dark:text-green-400">{{ Math.round(calculation.totalDistance / 1000) }} km</div>
          </div>
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">⏱️ {{ durationText }}</div>
            <div class="font-bold text-blue-600 dark:text-blue-400">{{ formatDuration(calculation.totalDuration) }}</div>
          </div>
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">💰 {{ fuelCostText }}</div>
            <div class="font-bold text-orange-600 dark:text-orange-400">{{ formatCurrency(convertedFuelCost, currency) }}</div>
          </div>
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">📍 {{ stopsCountText }}</div>
            <div class="font-bold text-purple-600 dark:text-purple-400">{{ stops.length }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import draggable from 'vuedraggable'
import type { Stop, TripCalculation, Vehicle } from '@/types'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { formatCurrency, convertCurrency } from '@/utils/currency'
import VehicleSelector from './VehicleSelector.vue'

interface Props {
  stops: Stop[]
  calculation?: TripCalculation | null
  vehicles?: Vehicle[]
  selectedVehicleId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  calculation: null,
  vehicles: () => [],
  selectedVehicleId: null
})

const { t } = useI18n()
const settingsStore = useSettingsStore()
const authStore = useAuthStore()
const { language, currency } = storeToRefs(settingsStore)

// Check if in demo mode - only when NOT authenticated
const isInDemoMode = computed(() => !authStore.isAuthenticated)

// Reactive translations
const stopsTitleText = computed(() => t('stopsTitle'))
const stopsText = computed(() => t('stops'))
const noStopsText = computed(() => t('noStops'))
const createFirstTripText = computed(() => t('createFirstTrip'))
const tripSummaryText = computed(() => t('tripSummary'))
const distanceText = computed(() => t('distance'))
const durationText = computed(() => t('duration'))
const fuelCostText = computed(() => t('fuelCost'))
const stopsCountText = computed(() => t('stopsTitle'))

// Convert fuel cost from EUR to selected currency
const convertedFuelCost = computed(() => {
  if (!props.calculation?.totalFuelCost) return 0
  // Assume totalFuelCost is stored in EUR
  return convertCurrency(props.calculation.totalFuelCost, 'EUR', currency.value)
})

const emit = defineEmits<{
  stopClick: [stop: Stop]
  editStop: [stop: Stop]
  deleteStop: [stopId: string]
  reorderStops: [stopIds: string[]]
  'update:vehicleId': [vehicleId: string | null]
  'add-vehicle': []
}>()

const localStops = ref([...props.stops])

watch(() => props.stops, (newStops) => {
  localStops.value = [...newStops].sort((a, b) => a.order - b.order)
}, { deep: true, immediate: true })

const stopTypeIcons: Record<string, string> = {
  city: '🏙️',
  gas_station: '⛽',
  ev_charging: '⚡',
  rest_area: '🚻',
  restaurant: '🍽️',
  fast_food: '🍔',
  cafe: '☕',
  hotel: '🏨',
  motel: '🛏️',
  camping: '⛺',
  attraction: '🎯',
  museum: '🏛️',
  park: '🌳',
  viewpoint: '🏔️',
  shopping: '🛍️',
  parking: '🅿️',
  other: '📍',
  // Legacy support
  accommodation: '🏨'
}

function getStopIcon(type: Stop['type']): string {
  return stopTypeIcons[type] || '📍'
}

function onDragEnd() {
  const stopIds = localStops.value.map(stop => stop.id)
  emit('reorderStops', stopIds)
}

function getSegmentDistance(segmentIndex: number): number {
  if (!props.calculation || !props.calculation.segments[segmentIndex]) {
    return 0
  }
  return props.calculation.segments[segmentIndex].distance
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  
  if (hours > 0) {
    return `${hours}h ${mins}m`
  }
  return `${mins}m`
}
</script>

<style scoped>
/* Sidebar container - full height */
.stops-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: var(--ion-background-color, #fff);
}

/* Header section */
.sidebar-header {
  padding: 0.75rem;
  border-bottom: 1px solid var(--ion-border-color, #e5e7eb);
  flex-shrink: 0;
}

/* Scrollable content */
.sidebar-content {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.5rem;
}

.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

:deep(.sortable-chosen) {
  @apply bg-gray-100 dark:bg-gray-600;
}

:deep(.sortable-ghost) {
  @apply opacity-50;
}
</style>